/**
 * Canonical trip-review service.
 *
 * One participant submits one rating for the complete trip. The database
 * derives the affected host, guest, and vehicle reputations from the rental.
 */
import { supabase } from '../supabase'
import type { Cols, RpcRow } from '../types/db-rows'
import { ServiceError } from '../errors/service-error'
import { BOOKING_LIMITS } from '../utils/constants'
import type {
  PendingTripReview,
  ReviewEligibility,
  SubmitTripReviewInput,
  SubmitTripReviewResult,
  TripReviewerRole,
  VehicleTripReview,
} from '../types/review.types'

type RentalStatusRow = Cols<
  'rentals',
  'id' | 'status' | 'end_at' | 'actual_end_at' | 'guest_id' | 'host_id'
> & {
  payments?: { status: string }[] | null
}

function tripReviewerRole(value: string): TripReviewerRole {
  if (value === 'guest' || value === 'host') return value
  throw ServiceError.validation('Unexpected trip reviewer role')
}

function mapPendingTripReview(row: RpcRow<'pending_trip_review_for_viewer'>): PendingTripReview {
  return {
    rentalId: row.rental_id,
    vehicleId: row.vehicle_id,
    vehicleTitle: row.vehicle_title,
    vehicleImage: row.vehicle_image || null,
    counterpartyId: row.counterparty_id,
    counterpartyName: row.counterparty_name,
    counterpartyAvatar: row.counterparty_avatar || null,
    reviewerRole: tripReviewerRole(row.reviewer_role),
    startedAt: row.started_at,
    endedAt: row.ended_at,
  }
}

function mapSubmitTripReview(row: RpcRow<'submit_trip_review'>): SubmitTripReviewResult {
  return {
    reviewId: row.review_id,
    rentalId: row.rental_id,
    vehicleId: row.vehicle_id,
    reviewerRole: tripReviewerRole(row.reviewer_role),
    rating: row.rating,
    comment: row.comment ?? null,
    createdAt: row.created_at,
  }
}

function mapVehicleTripReview(row: RpcRow<'get_vehicle_trip_reviews'>): VehicleTripReview {
  return {
    id: row.id,
    rentalId: row.rental_id,
    rating: row.rating,
    comment: row.comment ?? null,
    createdAt: row.created_at,
    reviewerDisplayName: row.reviewer_display_name ?? null,
    reviewerAvatarUrl: row.reviewer_avatar_url ?? null,
  }
}

class ReviewServiceImpl {
  /**
   * Check whether this participant still has a trip review to submit.
   *
   * This mirrors the RPC's server-side guard for immediate UI feedback. The
   * submit RPC remains authoritative and closes the race atomically.
   */
  async checkEligibility(userId: string, rentalId: string): Promise<ReviewEligibility> {
    const { data: rental, error: rentalError } = await supabase
      .from('rentals')
      .select('id, status, end_at, actual_end_at, guest_id, host_id, payments(status)')
      .eq('id', rentalId)
      .maybeSingle()

    if (rentalError) throw ServiceError.fromSupabaseError(rentalError)
    if (!rental) throw ServiceError.notFound('Booking')

    const typedRental: RentalStatusRow = rental
    if (typedRental.status !== 'completed') {
      return { canReview: false, reason: 'not_completed', windowClosesAt: null }
    }

    const isParticipant = typedRental.guest_id === userId || typedRental.host_id === userId
    if (!isParticipant) {
      return { canReview: false, reason: 'wrong_role', windowClosesAt: null }
    }

    if (!(typedRental.payments ?? []).some((payment) => payment.status === 'captured')) {
      return { canReview: false, reason: 'not_completed', windowClosesAt: null }
    }

    const endDate = new Date(typedRental.actual_end_at ?? typedRental.end_at)
    if (!Number.isFinite(endDate.getTime())) {
      throw ServiceError.validation('Booking has an invalid completion date')
    }

    const now = new Date()
    const windowClosesAt = new Date(
      endDate.getTime() + BOOKING_LIMITS.reviewWindowDays * 24 * 60 * 60 * 1000,
    )

    if (endDate.getTime() > now.getTime()) {
      return { canReview: false, reason: 'not_completed', windowClosesAt: null }
    }

    if (now.getTime() >= windowClosesAt.getTime()) {
      return {
        canReview: false,
        reason: 'window_closed',
        windowClosesAt: windowClosesAt.toISOString(),
      }
    }

    const { count, error: countError } = await supabase
      .from('reviews')
      .select('id', { count: 'exact', head: true })
      .eq('rental_id', rentalId)
      .eq('reviewer_id', userId)

    if (countError) throw ServiceError.fromSupabaseError(countError)

    if ((count ?? 0) > 0) {
      return {
        canReview: false,
        reason: 'already_reviewed',
        windowClosesAt: windowClosesAt.toISOString(),
      }
    }

    return {
      canReview: true,
      reason: 'eligible',
      windowClosesAt: windowClosesAt.toISOString(),
    }
  }

  /** Return the oldest completed trip the authenticated viewer has not rated. */
  async getPendingTripReview(): Promise<PendingTripReview | null> {
    const { data, error } = await supabase.rpc('pending_trip_review_for_viewer')
    if (error) throw ServiceError.fromSupabaseError(error)

    const row = (data ?? [])[0]
    return row ? mapPendingTripReview(row) : null
  }

  /**
   * Submit the complete trip rating through the idempotent, authenticated RPC.
   *
   * Identity and role are derived in Postgres; the client cannot select a
   * review subject or write reputation projections directly.
   */
  async submitTripReview(input: SubmitTripReviewInput): Promise<SubmitTripReviewResult> {
    if (!Number.isInteger(input.rating) || input.rating < 1 || input.rating > 5) {
      throw ServiceError.validation('Rating must be an integer between 1 and 5')
    }

    const comment = input.comment?.trim()
    if (comment && comment.length > 1000) {
      throw ServiceError.validation('Comment must be 1000 characters or fewer')
    }

    const { data, error } = await supabase.rpc('submit_trip_review', {
      p_rental_id: input.rentalId,
      p_rating: input.rating,
      ...(comment ? { p_comment: comment } : {}),
    })

    if (error) throw ServiceError.fromSupabaseError(error)

    const row = (data ?? [])[0]
    if (!row) {
      throw ServiceError.validation('Trip review submission returned no result')
    }

    return mapSubmitTripReview(row)
  }

  /** Get guest-authored trip experiences projected onto a vehicle. */
  async getReviewsForVehicle(vehicleId: string, limit = 10): Promise<VehicleTripReview[]> {
    const { data, error } = await supabase.rpc('get_vehicle_trip_reviews', {
      p_vehicle_id: vehicleId,
      p_limit: limit,
    })

    if (error) throw ServiceError.fromSupabaseError(error)
    return (data ?? []).map(mapVehicleTripReview)
  }
}

export const reviewService = new ReviewServiceImpl()
